import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import Base64Tool from '../components/Base64Tool';
import FaqSection from '../components/Faq';
import { PageHero } from '../components/InfoSections';
import { useTheme } from '../theme';
import { softwareApplicationSchema, faqPageSchema, breadcrumbSchema, Faq } from '../lib/schema';

const FAQS: Faq[] = [
  { q: 'How do I decode a Base64 string?', a: 'Paste your Base64 string into the input box above. The decoded, human-readable text appears instantly on the right.' },
  { q: 'Why do I get an "Invalid Base64 string" error?', a: 'That error means the input is not valid Base64 — it may contain illegal characters, be truncated, or have incorrect padding. Double-check that you copied the full string. If it uses "-" and "_", decode it with the URL-safe tool instead.' },
  { q: 'Can this decode URL-safe Base64?', a: 'This page decodes standard Base64. For strings using the URL-safe alphabet ("-" and "_" instead of "+" and "/"), use the dedicated URL-safe Base64 decoder.' },
  { q: 'Does decoding Base64 reveal the original data exactly?', a: 'Yes. Base64 is a lossless, reversible encoding, so decoding returns the exact original bytes — including UTF-8 text, emoji and non-Latin characters.' },
];

export default function DecodePage() {
  const { themeClasses } = useTheme();
  return (
    <>
      <Seo
        title="Base64 Decode — Convert Base64 to Text Online | EasyBase64"
        description="Decode Base64 to text online for free. Paste a Base64 string and get the original text instantly. UTF-8 safe, private and runs entirely in your browser."
        path="/base64-decode"
        jsonLd={[
          softwareApplicationSchema({
            name: 'Base64 Decoder',
            description: 'Free online tool to decode Base64 to text instantly in the browser.',
            path: '/base64-decode',
          }),
          faqPageSchema(FAQS),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Base64 Decode', path: '/base64-decode' },
          ]),
        ]}
      />

      <PageHero
        title="Base64 Decode Online"
        subtitle={<>Convert a Base64 string back into readable text instantly. Paste below — need to go the other way? Try the <Link to="/base64-encode" className="underline">Base64 encoder</Link>.</>}
      />

      <div className="mb-12">
        <Base64Tool initialMode="decode" variant="standard" />
      </div>

      <section className={`${themeClasses.infoCard} rounded-2xl p-5 sm:p-8 border mb-12`}>
        <h2 className={`text-2xl font-bold ${themeClasses.textPrimary} mb-4`}>How to decode Base64</h2>
        <ol className={`list-decimal list-inside space-y-2 ${themeClasses.textSecondary} leading-relaxed`}>
          <li>Paste your Base64 string into the input field above.</li>
          <li>The decoded text appears automatically on the right.</li>
          <li>Click <strong>Copy</strong> to copy the result, or <strong>Save</strong> to download it.</li>
          <li>If you see an error, check the string for missing characters or decode it as <Link to="/url-safe-base64" className="underline">URL-safe Base64</Link>.</li>
        </ol>
      </section>

      <FaqSection title="Base64 decoding FAQ" faqs={FAQS} />
    </>
  );
}
